import { NotificationInline } from '@platform-ui/notification';
import { NotificationBaseProps } from '@platform-ui/notification/NotificationBase/NotificationBase';
import { getTimerDistance } from 'components-machine';
import React, { useEffect, useState } from 'react';
import { FormattedMessage as Message } from 'react-intl';
import Sticky from 'react-sticky-el';
import { getTimerHeight, useTimerHeightObserver } from '../../utils/notifyIframeHeight';
import styles from './PageTimer.module.css';
import { timeToString } from './utils';

interface PageTimerProps {
  createdAt: number;
  timerInfo: {
    timerDuration?: number;
    timerStrict: boolean;
    timerExpiredText?: string;
  };
  updateTimerHeight: (height: number) => void;
}

const PageTimer = ({ updateTimerHeight, createdAt, timerInfo }: PageTimerProps) => {
  const timerRef = useTimerHeightObserver(updateTimerHeight);

  const [isFinished, setFinished] = useState(false);

  const handleFinish = () => {
    setFinished(true);
  };

  const getTypeNotify = (): NotificationBaseProps['type'] => {
    if (isFinished && timerInfo.timerStrict) return 'error';
    if (isFinished && !timerInfo.timerStrict) return 'warning';

    return 'info';
  };

  return (
    <div className={styles.rootWrapper} style={{ height: getTimerHeight() }}>
      <Sticky className={styles.sticky} topOffset={-16} stickyStyle={{ top: '16px' }}>
        <div id="timer" ref={timerRef} className={styles.root}>
          <NotificationInline showClose={false} timer={false} type={getTypeNotify()}>
            {isFinished ? (
              timerInfo.timerExpiredText || <Message id={'PageTimer.ExpiredText'} />
            ) : (
              <>
                <Message id={'PageTimer.UntilTitle'} />{' '}
                <span className={styles.timer}>
                  <Countdown duration={timerInfo.timerDuration ?? 0} startedAt={createdAt} onFinish={handleFinish} />
                </span>
              </>
            )}
          </NotificationInline>
        </div>
      </Sticky>
    </div>
  );
};

const Countdown: React.FC<{
  onFinish: VoidFunction;
  startedAt: number;
  duration: number;
}> = ({ duration, startedAt, onFinish }) => {
  const [distance, setDistance] = useState(() => getTimerDistance(startedAt, duration));

  useEffect(() => {
    const intervalId = setInterval(() => {
      const distance = getTimerDistance(startedAt, duration);
      if (distance > 0) {
        setDistance(distance);
      } else {
        clearInterval(intervalId);
        setDistance(0);
        onFinish();
      }
    }, 1000);
    return () => {
      clearInterval(intervalId);
    };
  }, [startedAt, duration]);

  return <>{timeToString(distance)}</>;
};

export default PageTimer;
